import { Brackets, In } from 'typeorm'
import { Resolver, Query, FieldResolver, Root, Args, Arg, Ctx, Directive } from 'type-graphql'
import { Domain, getQueryBuilderFromListParams, getRepository, ListParam } from '@things-factory/shell'
import { User } from '@things-factory/auth-base'
import { BoardTemplate } from './board-template.js'
import { BoardTemplateList } from './board-template-type.js'

@Resolver(BoardTemplate)
export class BoardTemplateQuery {
  @Query(returns => BoardTemplate!, { nullable: true, description: 'To fetch a BoardTemplate' })
  @Directive('@privilege(category: "board-template", privilege: "query", domainOwnerGranted: true)')
  async boardTemplate(@Arg('id') id: string, @Ctx() context: ResolverContext): Promise<BoardTemplate> {
    const { domain, user } = context.state

    const qb = await getRepository(BoardTemplate)
      .createQueryBuilder('BoardTemplate')
      .where('id=:id', { id })
      .andWhere(
        new Brackets(qb => {
          qb.where({ visibility: 'public' })
            .orWhere({
              visibility: 'domain',
              domain: { id: In([domain.id, domain.parentId]) }
            })
            .orWhere({
              visibility: 'private',
              creator: { id: user.id }
            })
        })
      )

    return qb.getOne()

    // return await getRepository(BoardTemplate).findOne({
    //   where: {
    //     domain: { id: In([domain.id, domain.parentId].filter(Boolean)) },
    //     id
    //   }
    // })
  }

  @Query(returns => BoardTemplateList, { description: 'To fetch BoardTemplates created by me' })
  async boardTemplatesCreatedByMe(
    @Args(type => ListParam) params: ListParam,
    @Ctx() context: ResolverContext
  ): Promise<BoardTemplateList> {
    const { user } = context.state

    const queryBuilder = getQueryBuilderFromListParams({
      repository: getRepository(BoardTemplate),
      params,
      alias: 'template',
      searchables: ['name', 'description']
    }).andWhere('template.creator = :user', { user: user.id })

    const [items, total] = await queryBuilder.getManyAndCount()

    return { items, total }
  }

  @Query(returns => BoardTemplateList, { description: 'To fetch multiple BoardTemplates' })
  @Directive('@privilege(category: "board-template", privilege: "query", domainOwnerGranted: true)')
  async boardTemplates(
    @Args(type => ListParam) params: ListParam,
    @Ctx() context: ResolverContext
  ): Promise<BoardTemplateList> {
    const { domain, user } = context.state
    /* 
      리스트에 포함되는 보드템플릿들 
      - visibility가 public 인 경우
      - visibility가 domain이며 컨텍스트의 도메인과 같은 경우
      - visibility가 private 이며, creator가 나인 경우
    */

    const queryBuilder = getQueryBuilderFromListParams({
      params,
      repository: await getRepository(BoardTemplate),
      searchables: ['name', 'description']
    }).andWhere(
      new Brackets(qb => {
        qb.where({ visibility: 'public' })
          .orWhere({
            visibility: 'domain',
            domain: { id: In([domain.id, domain.parentId]) }
          })
          .orWhere({
            visibility: 'private',
            creator: { id: user.id }
          })
      })
    )

    const [items, total] = await queryBuilder.getManyAndCount()

    return { items, total }
  }

  @FieldResolver(type => Boolean)
  async mine(@Root() boardTemplate: BoardTemplate, @Ctx() context: ResolverContext): Promise<boolean> {
    const { creatorId } = boardTemplate
    const { user } = context.state

    return creatorId == user.id
  }

  @FieldResolver(type => Domain)
  async domain(@Root() boardTemplate: BoardTemplate): Promise<Domain> {
    return boardTemplate.domainId && (await getRepository(Domain).findOneBy({ id: boardTemplate.domainId }))
  }

  @FieldResolver(type => User)
  async updater(@Root() boardTemplate: BoardTemplate): Promise<User> {
    return boardTemplate.updaterId && (await getRepository(User).findOneBy({ id: boardTemplate.updaterId }))
  }

  @FieldResolver(type => User)
  async creator(@Root() boardTemplate: BoardTemplate): Promise<User> {
    return boardTemplate.creatorId && (await getRepository(User).findOneBy({ id: boardTemplate.creatorId }))
  }
}
